import {Tr} from "@/i18n/i18n";
import {VerticalStandardPanel} from "@/scenes.ui/vertical-standard-panel";
import {ResourceType} from "@/store/place/types";
import ResourceUtils from "@/utils/resource-utils";
import TextUtils from "@/utils/text-utils";
import {Fonts} from "@/constants/font";
import {Keys} from "@/constants/key";
import {SquareButton} from "@/scenes.ui/square-button";
import {FeedbackPanel} from "@/scenes.ui/feedback-panel";
import Scene = Phaser.Scene;
import {ClanStat, DepositDto, DepositPortion} from "@/store/clan/types";
import {Player} from "@/store/player/types";
import {CalculationType} from "@/scenes.ui/exchange-panel";
import BaseSound = Phaser.Sound.BaseSound;
import Container = Phaser.GameObjects.Container;
import Text = Phaser.GameObjects.Text;
import SoundUtils from "@/utils/sound-utils";
import SpriteUtils from "@/utils/sprite-utils";
import CU from "@/utils/c-u";

export class ClanDepositPanel extends VerticalStandardPanel {

    private selectSound!: BaseSound | undefined;

    private playerResources: Map<ResourceType, number> = new Map<ResourceType, number>();

    private depositByType: Map<ResourceType, number> = new Map<ResourceType, number>();

    private donatedResourceTypeByText: Map<ResourceType, Text> = new Map<ResourceType, Phaser.GameObjects.Text>();

    private confirmCallback!: (donateDto: DepositDto) => void;

    constructor(scene: Scene, canvas: HTMLCanvasElement, clanStat: ClanStat, player: Player, confirmCallback: (depositDto: DepositDto) => void) {
        super(scene, canvas, Tr('popolnenie.kazni'));

        this.selectSound = SoundUtils.addSound(scene, Keys.SOUND_SELECT);

        this.confirmCallback = confirmCallback;

        this.playerResources = ResourceUtils.convertToBalance(player);

        this.setupMaterial(scene, -170, -160, Keys.IMG_RES_MITHRIL, Tr('izumrudi'), ResourceType.MITHRIL, this.getInitValue(player.mithril));

        this.setupMaterial(scene, -170, -80, Keys.IMG_RES_ELIXIR, Tr('sinie.krist'), ResourceType.ELIXIR, this.getInitValue(player.elixir));

        this.setupMaterial(scene, -170, 0, Keys.IMG_RES_CRYSTAL, Tr('krasn.krist.'), ResourceType.CRYSTAL, this.getInitValue(player.crystal));

        this.setupMaterial(scene, -170, 80, Keys.IMG_RES_GOLD, Tr('zoloto'), ResourceType.GOLD, this.getInitValue(player.gold));

        this.addCustomButton(0, 180, Tr('pozhertvovat'), Keys.UI_ART_BROWN_BUTTON, Keys.ATLAS_PROFILE, Keys.UI_GOLDEN_CHECK, () => {
            if (this.ifSomethingWasSpend()) {
                if (this.confirmCallback != null) {
                    this.confirmCallback(this.convertToDepositDto(this.depositByType));
                    this.closeWithCallback();
                }
            } else {
                new FeedbackPanel(scene, Tr('snachala.nuzhno.chtoto.vnesti'), undefined, false).show();
            }
        }, true);

    }

    private getInitValue(value: number): number {
        return 0;
    }

    private setupMaterial(scene: Scene, x: number, y: number, iconKey: string, titleText: string, resourceType: ResourceType, initValue: number) {

        let icon = SpriteUtils.addImage(scene, x,  y, iconKey);

        let text = TextUtils.addTextAdvanced(this.scene, titleText, icon.getCenter().x! / CU.S + 40, icon.getTopRight().y! / CU.S + 10,
            '#eec761', 13, 'normal', 150, 'left', 2, Fonts.MYRIADPRO_BOLD);

        this.depositByType.set(resourceType, initValue);

        this.addActionButtons(80, icon.getCenter().y! / CU.S, resourceType);

        let donatedText = TextUtils.addTextAdvanced(this.scene, Tr('vnesti'), text.x / CU.S, icon.getBottomRight().y! / CU.S - 10,
            '#929292', 13, 'normal', 150, 'left', 2, Fonts.MYRIADPRO_BOLD);

        // let arrow = SpriteUtils.addImage(this.scene, 5, donatedText.getTopCenter().y - 9, Keys.UI_ART_ARR)
        //     .setFlipX(true)

        let donatedAmount = TextUtils.addTextAdvanced(this.scene, initValue.toString(),
            donatedText.getRightCenter().x! / CU.S + 20, donatedText.y / CU.S,
            '#ffdfdf', 13, 'normal', 150, 'left', 2, Fonts.MYRIADPRO_BOLD);

        this.donatedResourceTypeByText.set(resourceType, donatedAmount);

        this.add([icon, text, donatedText, donatedAmount]);
    }

    private addActionButtons(x: number, y: number, targetType: ResourceType) {

        const minusButt = new SquareButton(this.scene, x, y, 0, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_UI, Keys.UI_WHITE_MINUS, '', false, 40, 40);
        SpriteUtils.scale(minusButt, 0.35);

        minusButt.setCallback(() => {
            if (this.isOnTop()) {
                SoundUtils.play(this.selectSound);
                this.changeConvertedAmount(targetType, CalculationType.DECREASE);
            }
            this.refreshPlusMinusVisibility(plusButt, minusButt, targetType);
        });

        const plusButt = new SquareButton(this.scene, minusButt.x / CU.S + 90, minusButt.y / CU.S, 0, Keys.UI_ART_GREEN_BUTTON,
            Keys.ATLAS_UI, Keys.UI_WHITE_PLUS, '', false, 40, 40);
        SpriteUtils.scale(plusButt, 0.35);

        plusButt.setCallback(() => {
            if (this.isOnTop()) {
                SoundUtils.play(this.selectSound);
                this.changeConvertedAmount(targetType, CalculationType.INCREASE);
            }
            this.refreshPlusMinusVisibility(plusButt, minusButt, targetType);
        });

        this.add([minusButt, plusButt]);
        this.refreshPlusMinusVisibility(plusButt, minusButt, targetType);
    }

    private refreshPlusMinusVisibility(plusButt: Container, minusButt: Container, targetType: ResourceType) {
        let canInc = this.canConvertAmount(targetType, CalculationType.INCREASE);
        let canDec = this.canConvertAmount(targetType, CalculationType.DECREASE);
        plusButt.setAlpha(canInc ? 1 : 0.3);
        minusButt.setAlpha(canDec ? 1 : 0.3);
    }

    private changeConvertedAmount(targetType: ResourceType, calcType: CalculationType) {

        return this.processConvertedAmount(targetType, calcType, (resDelta: number) => {
            this.doChangeAmount(targetType, resDelta);
        }, () => {
            if (calcType === CalculationType.INCREASE) {
                new FeedbackPanel(this.scene, Tr('nedostatochno.resursov'), undefined, false).show();
            }
        });
    }

    private canConvertAmount(targetType: ResourceType, calcType: CalculationType) {
        return this.processConvertedAmount(targetType, calcType, () => {
            return true;
        }, () => {
            return false;
        });
    }

    private processConvertedAmount(targetType: ResourceType, calcType: CalculationType,
                    successCallback: (resDelta: number) => unknown, failCallback: () => unknown) {

        let resDelta = Math.ceil(this.playerResources.get(targetType)! * (targetType === ResourceType.MITHRIL ? 0.01 : 0.05));

        switch (calcType) {
            case CalculationType.INCREASE:
                if (this.checkIfPossibleToConvertTo(targetType, resDelta)) {
                    return successCallback(resDelta);
                }
                break;
            case CalculationType.DECREASE:
                if (this.checkIfPossibleToConvertBack(targetType, resDelta)) {
                    return successCallback(-resDelta);
                }
                break;
        }

        return failCallback();
    }

    private checkIfPossibleToConvertTo(resType: ResourceType, resDelta: number): boolean {
        return this.depositByType.get(resType)! + resDelta <= this.playerResources.get(resType)!;
    }

    private checkIfPossibleToConvertBack(resType: ResourceType, resDelta: number): boolean {
        return this.depositByType.get(resType)! - resDelta >= 0;
    }

    private doChangeAmount(targetType: ResourceType, resDelta: number) {

        let donated = this.depositByType.get(targetType)! + resDelta;

        this.depositByType.set(targetType, donated);

        this.donatedResourceTypeByText.get(targetType)!.setText(TextUtils.formatMoney(donated));

    }

    private convertToDepositDto(donatedAmountByType: Map<ResourceType, number>): DepositDto {
        let donateDto = new DepositDto();
        donatedAmountByType.forEach((value, key) => {
            donateDto.depositPortions.push(new DepositPortion(key, value))
        });
        return donateDto;
    }

    private ifSomethingWasSpend(): boolean {
        for (let [key, value] of this.depositByType) {
            if (value > 0) {
                return true;
            }
        }
        return false;
    }

}
